import React, { ReactElement, useState } from "react";
import {
    useListUserGroupsQuery, UserGroup,
} from "../../../__generated__/api";
import DisplayUserGroup from "../../creators/user-groups/UserGroups";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

export interface UserGroupsPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    canCreate?: boolean;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        values: (TIsMulti extends true ? string[] : string)) => void;
}

const UserGroups = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, canCreate, isClearable, className,
}: UserGroupsPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useListUserGroupsQuery();

    const [isShowCreate, setIsShowCreate] = useState(false);
    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<UserGroup>[] : Option<UserGroup>;
    type ValueType = TIsMulti extends true ? Option<UserGroup>[] : Option<UserGroup>;

    const handleSelected = (selected: SelectedType) => {
        let values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<UserGroup>).value) as Parameters<typeof onSelected>[0];

        if (values === "new" || (Array.isArray(values) && values.includes("new"))) {
            setIsShowCreate(true);
            if (Array.isArray(values)) {
                values = values.filter(row => row !== "new") as typeof values;
            } else {
                return;
            }
        }
        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label as string)
            : (selected as Option<UserGroup>).label) as Parameters<typeof onSelected>[1];

        onSelected(values, texts);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values: Option<Partial<UserGroup>>[] = data?.userGroups?.map(group => ({
        label: group.name as string,
        value: group.id.toString() as string,
        data: group,
    })) ?? [];

    let options: (Option<Partial<UserGroup>> | OptionWithChildren<Partial<UserGroup>>)[] = values;

    if (canCreate) {
        options = [
            {
                label: "Existing Groups",
                options: values,
            },
            {
                label: "Create",
                options: [
                    {
                        label: "Create new group",
                        value: "new",
                    },
                ],
            },
        ];
    }
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <>
            {isShowCreate && (

                <DisplayUserGroup
                    isModal
                    onHide={() => setIsShowCreate(false)}
                    onSave={group => {
                        // stuff

                        if (isMulti && Array.isArray(selected)) {
                            const copy = selected.toIdList(row => row.value);
                            copy.push(group.id);
                            onSelected(
                                copy as Parameters<typeof onSelected>[0],
                                selected.toIdList(row => row.label as string) as Parameters<typeof onSelected>[1],
                            );
                        } else {
                            onSelected(group.id, group.name as Parameters<typeof onSelected>[1]);
                        }

                        setIsShowCreate(false);
                    }}
                />
            )}

            <Select
                ref={ref}
                minRequired={minRequired}
                placeholder={placeholder}
                isMulti={isMulti}
                onChange={handleSelected}
                options={(options ?? []) as Option<UserGroup>[]}
                value={selected as ValueType}
                isClearable={isClearable}
                className={className}
            />
        </>
    );
});

UserGroups.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    canCreate: undefined,
    isClearable: undefined,
    className: "",
};

export default UserGroups as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
> (
    props: UserGroupsPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof UserGroups>;
